import type { CartItem, CheckoutData, OrderItem } from "@lemmonhq/types";
import {
  calculateCartItemTotal,
  calculateOrderItemTotal,
  formatPrice,
} from "@lemmonhq/orders";
import type { DiscountableItem } from "./types";

export const extractDiscountableItems = (
  checkoutData: CheckoutData,
  currency: string,
): DiscountableItem[] => {
  const payload = checkoutData.payload;
  if (!payload) return [];

  if (payload.type === "cart") {
    return payload.object.items.map((item: CartItem) => {
      const total = calculateCartItemTotal(item);
      const unitPrice = item.quantity > 0 ? total / item.quantity : total;

      return {
        key: `cart:${item.cartId}`,
        label: item.name,
        detail: `${item.quantity} × ${formatPrice(unitPrice, true, 2, currency)}`,
        total,
        quantity: item.quantity,
        cartItemId: item.cartId,
      };
    });
  }

  if (payload.type !== "table") return [];

  const tableCheckout = payload.object as any;
  if (tableCheckout.type === "split") return [];

  const orders: any[] = tableCheckout.orders ?? [];
  const orderItems = orders.flatMap((order) => order.items ?? []);

  if (tableCheckout.type === "partial") {
    return (tableCheckout.items ?? []).map((partialItem: any) => {
      const item = orderItems.find(
        (orderItem: OrderItem) => orderItem.id === partialItem.id,
      ) as OrderItem | undefined;
      const order = orders.find((order) => order.id === partialItem.orderId);
      const quantity = partialItem.quantity;
      const total = partialItem.total;
      const unitPrice = quantity > 0 ? total / quantity : total;

      return {
        key: `order:${partialItem.id}`,
        label: item?.product?.name ?? `Item ${partialItem.id}`,
        detail: `#${order?.display_id ?? partialItem.orderId} · ${quantity} × ${formatPrice(unitPrice, true, 2, currency)}`,
        total,
        quantity,
        orderItemId: String(partialItem.id),
      };
    });
  }

  return orders.flatMap((order) =>
    (order.items ?? []).map((item: OrderItem) => ({
      key: `order:${item.id}`,
      label: item.product?.name ?? `Item ${item.id}`,
      detail: `#${order.display_id} · ${item.quantity} × ${formatPrice(Number(item.price), true, 2, currency)}`,
      total: calculateOrderItemTotal(item),
      quantity: item.quantity,
      orderItemId: String(item.id),
    })),
  );
};
